import React, { useRef } from 'react'
import ScrollContainer from 'react-indiana-drag-scroll'

import HitLocationSurface from 'components/hit-location-surface/hit-location-surface'
import { ExerciseDataStyle, INRWorkoutSessionExercise, INRWorkoutSessionHit } from 'interfaces/workout'
import { getMaxForce } from 'lib/stats'
import { convertHitsToTimedHits, getPercentage } from 'lib/utilities'
import { DisplayForceHit, getExerciseDisplayForceHits, usesCombinationData } from 'lib/hit-combinations'
import { useCurrentCount, useCurrentExercise } from 'store/workout/selectors'
import Bar from './bar-exercise'

import './chart-rounds.scss'

const Bars = ({ exercise, currentCount }: { exercise: INRWorkoutSessionExercise; currentCount: number }) => {
  let start = 0
  const end = exercise.seconds - 1

  const barsPerSecond = []
  const displayHits = getExerciseDisplayForceHits(exercise)
  const maxPercentage = Math.max(...displayHits.map((hit) => getPercentage(hit.force)), 0)
  const timedHits = convertHitsToTimedHits(displayHits, exercise.started) as Record<number, DisplayForceHit[]>

  while (start <= end) {
    const currentStart = start;
    let bars = null
    if (timedHits[start]) {
      bars = timedHits[currentStart].map((hit: DisplayForceHit, hitIndex: number) => {
        const height = maxPercentage ? (getPercentage(hit.force) / maxPercentage) * 100 : 0
        return (
          <Bar
            combination={hit.combination}
            height={height}
            key={`${hit.punchedAt}-${hitIndex}`}
            segments={hit.segments}
          />
        )
      })
    } else {
      bars = null
    }

    if ((bars && bars.length) || exercise.seconds - currentCount > start - 1) {
      barsPerSecond.push(
        <div className={`ChartEx__roundssec ${start}`} key={`${start}-${start}`}>
          {bars}
        </div>
      )
    }
    start++
  }

  return <>{barsPerSecond}</>
}

const ChartExercise = () => {
  const container = useRef<HTMLElement>(null)
  const currentExercise = useCurrentExercise()
  const currentCount = useCurrentCount()
  const displayHits = getExerciseDisplayForceHits(currentExercise)
  const showCombinations = usesCombinationData(currentExercise)
  const showLocation = currentExercise.dataStyle === ExerciseDataStyle.LOCATION_DATA

  if (showLocation) {
    return <HitLocationSurface hit={currentExercise.hits[currentExercise.hits.length - 1]} />
  }

  const maxForce = getMaxForce(displayHits)

  const calcAverage = () => {
    let total: number = 0
    displayHits.forEach((b: INRWorkoutSessionHit) => (total += getPercentage(b.force)))
    return Math.round(total / displayHits.length) + '%'
  }

  return (
    <div className="ChartEx history">
      <div className="ChartEx__wrapper">
        <div className="ChartEx__yaxis">
          <span>Force</span>
          <span>{Math.round(maxForce)}</span>
        </div>
        <div className="ChartEx__bars">
          {!showCombinations &&
            Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className={`ChartEx__yaxis__line ChartEx__yaxis__line--${index + 1}`} />
            ))}
          {!showCombinations && currentExercise.hits.length > 0 && (
            <div className="ChartEx__average" style={{ bottom: `calc(${calcAverage()} + 7px)` }} />
          )}
          <ScrollContainer innerRef={container} className="ChartEx__scrollable" vertical={false}>
            <div className="ChartEx__roundsbars">{<Bars currentCount={currentCount} exercise={currentExercise} />}</div>
          </ScrollContainer>
        </div>
      </div>
    </div>
  )
}

export default ChartExercise
